import { useEffect, useState } from "react";
import { mntAPI, PRIO_STYLE, STATUS_STYLE } from "./mntApi";
import { toast } from "sonner";
import { Plus, X, Save, Play, Pause, CheckCircle2, XCircle, Lock, Clock } from "lucide-react";
import { NumberInput } from "@/components/ui/number-input";

export default function MaintenanceWorkOrders() {
  const [rows, setRows] = useState([]);
  const [assets, setAssets] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => mntAPI.wos({
    status: status || undefined, priority: priority || undefined
  }).then(setRows).catch(() => {});
  useEffect(() => { load(); }, [status, priority]); // eslint-disable-line
  useEffect(() => {
    mntAPI.assets({ limit: 500 }).then(setAssets);
    mntAPI.engineers().then(setEngineers);
  }, []);

  return (
    <div className="space-y-4" data-testid="mnt-wos">
      <div className="flex flex-wrap items-center gap-3">
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="mnt-wo-status-filter"
                className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]">
          <option value="">All statuses</option>
          {Object.entries(STATUS_STYLE).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={priority} onChange={e => setPriority(e.target.value)} data-testid="mnt-wo-prio-filter"
                className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]">
          <option value="">All priorities</option>
          {Object.entries(PRIO_STYLE).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button onClick={() => setCreating(true)} disabled={!assets.length}
                data-testid="mnt-wo-new"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-50">
          <Plus className="w-4 h-4"/> New Work Order
        </button>
      </div>

      {!rows.length ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center text-[13px] text-slate-500">
          No work orders match the current filters.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Asset</th>
                <th className="text-left px-4 py-3">Priority</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Assigned</th>
                <th className="text-left px-4 py-3">SLA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(w => {
                const p = PRIO_STYLE[w.priority] || PRIO_STYLE.medium;
                const s = STATUS_STYLE[w.status] || STATUS_STYLE.submitted;
                const breached = w.sla_deadline_at && w.sla_deadline_at < new Date().toISOString() &&
                                 !["completed","closed","cancelled"].includes(w.status);
                return (
                  <tr key={w.id} onClick={() => setDetail(w)}
                      data-testid={`mnt-wo-row-${w.id}`}
                      className="hover:bg-slate-50/50 cursor-pointer">
                    <td className="px-4 py-3 mono text-slate-500">{w.code}</td>
                    <td className="px-4 py-3 font-medium text-hocp-navy truncate max-w-[280px]">{w.title}</td>
                    <td className="px-4 py-3 text-slate-600 truncate max-w-[180px]">{w.asset_snapshot?.name || "—"}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${p.chip.replace("ring-1","").replace(/ring-\S+/g,"")}`}>{p.label}</span></td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${s.chip}`}>{s.label}</span></td>
                    <td className="px-4 py-3 text-slate-600 text-[12px] truncate max-w-[150px]">{w.assigned_to_name || w.assigned_to_email || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3">
                      {breached
                        ? <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-rose-100 text-rose-700 inline-flex items-center gap-1"><Clock className="w-3 h-3"/> breached</span>
                        : w.sla_hours ? <span className="text-[12px] text-slate-500">{w.sla_hours}h</span>
                                       : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <WOCreateDialog assets={assets}
                        onClose={() => setCreating(false)}
                        onDone={() => { setCreating(false); load(); }}/>
      )}
      {detail && (
        <WODetailDialog woId={detail.id} engineers={engineers}
                        onClose={() => setDetail(null)}
                        onDone={() => { setDetail(null); load(); }}/>
      )}
    </div>
  );
}

function WOCreateDialog({ assets, onClose, onDone }) {
  const [f, setF] = useState({
    title: "", description: "", asset_id: "", priority: "medium",
    kind: "corrective", category: "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      await mntAPI.createWO({
        title: f.title, description: f.description || undefined,
        asset_id: f.asset_id || undefined, priority: f.priority,
        kind: f.kind, category: f.category || undefined,
      });
      toast.success("Work order created"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="New Work Order" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <F label="Title" full><input value={f.title} onChange={e => setF({...f, title: e.target.value})} data-testid="new-wo-title" className={cls}/></F>
        <F label="Description" full><textarea rows={2} value={f.description} onChange={e => setF({...f, description: e.target.value})} className={cls + " py-2 h-auto"}/></F>
        <F label="Asset"><select value={f.asset_id} onChange={e => setF({...f, asset_id: e.target.value})} data-testid="new-wo-asset" className={cls}>
          <option value="">— none —</option>
          {assets.map(a => <option key={a.id} value={a.id}>{a.asset_code} — {a.name}</option>)}
        </select></F>
        <F label="Priority"><select value={f.priority} onChange={e => setF({...f, priority: e.target.value})} data-testid="new-wo-priority" className={cls}>
          <option value="emergency">Emergency</option><option value="high">High</option>
          <option value="medium">Medium</option><option value="low">Low</option>
        </select></F>
        <F label="Kind"><select value={f.kind} onChange={e => setF({...f, kind: e.target.value})} className={cls}>
          <option value="corrective">Corrective</option>
          <option value="preventive">Preventive</option>
          <option value="inspection">Inspection</option>
          <option value="installation">Installation</option>
        </select></F>
        <F label="Category"><input value={f.category} onChange={e => setF({...f, category: e.target.value})} placeholder="Electrical / HVAC / Plumbing…" className={cls}/></F>
      </div>
      <Footer onClose={onClose} onSubmit={submit} saving={saving} testid="new-wo-submit"/>
    </Modal>
  );
}

function WODetailDialog({ woId, engineers, onClose, onDone }) {
  const [wo, setWo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [assignee, setAssignee] = useState("");
  const [showComplete, setShowComplete] = useState(false);
  const [resolveNotes, setResolveNotes] = useState("");
  const [labourMin, setLabourMin] = useState(0);

  const load = () => mntAPI.wo(woId).then(setWo).catch(() => {});
  useEffect(() => { load(); }, [woId]); // eslint-disable-line

  if (!wo) return (
    <Modal title="Loading…" onClose={onClose}>
      <div className="p-8 text-center text-[13px] text-slate-400">Loading work order…</div>
    </Modal>
  );

  const p = PRIO_STYLE[wo.priority] || PRIO_STYLE.medium;
  const s = STATUS_STYLE[wo.status] || STATUS_STYLE.submitted;

  const act = async (fn, label) => {
    setBusy(true);
    try { await fn(); toast.success(label); load(); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`Work Order ${wo.code}`} onClose={onClose} wide>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h4 className="font-display text-[17px] font-semibold text-hocp-navy">{wo.title}</h4>
            {wo.description && <p className="text-[13px] text-slate-600 mt-1.5">{wo.description}</p>}
          </div>
          <div className="text-right shrink-0 space-y-1.5">
            <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] font-medium ${p.chip.replace("ring-1","").replace(/ring-\S+/g,"")}`}>{p.label}</span>
            <br/><span className={`inline-block px-2 py-0.5 rounded-md text-[11px] font-medium ${s.chip}`}>{s.label}</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 text-[12.5px]">
          <Info label="Asset">{wo.asset_snapshot?.name || "—"}</Info>
          <Info label="Kind">{wo.kind}</Info>
          <Info label="Category">{wo.category || "—"}</Info>
          <Info label="Reported by">{wo.reported_by_email}</Info>
          <Info label="Assigned to">{wo.assigned_to_name || wo.assigned_to_email || <span className="text-slate-300">—</span>}</Info>
          <Info label="SLA">{wo.sla_hours ? `${wo.sla_hours}h` : "—"}</Info>
        </div>

        {/* Timeline */}
        <div>
          <div className="text-[11.5px] uppercase tracking-wider text-slate-500 font-medium mb-2">Timeline</div>
          <div className="space-y-2 max-h-[240px] overflow-y-auto">
            {(wo.events || []).map(e => (
              <div key={e.id} className="flex gap-3 items-start">
                <div className="w-1.5 h-1.5 rounded-full bg-hocp-navy mt-1.5 shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-medium text-hocp-navy">
                    {e.type} <span className="text-[11px] text-slate-400 font-normal">· by {e.actor_email}</span>
                  </div>
                  {e.notes && <div className="text-[12px] text-slate-600 mt-0.5">{e.notes}</div>}
                  <div className="text-[10.5px] mono text-slate-400 mt-0.5">{e.at?.slice(0,16).replace("T"," ")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Assign panel */}
        {showAssign && (
          <div className="rounded-lg bg-sky-50/60 border border-sky-100 p-3">
            <div className="text-[12px] font-medium text-hocp-navy mb-2">Pick engineer</div>
            <select value={assignee} onChange={e => setAssignee(e.target.value)}
                    data-testid="wo-assign-pick"
                    className={cls}>
              <option value="">— select —</option>
              {engineers.map(en => <option key={en.id} value={en.id}>{en.name || en.email} ({en.open_wo_count} open)</option>)}
            </select>
            <div className="mt-2 flex gap-2 justify-end">
              <button onClick={() => setShowAssign(false)} className="h-8 px-3 rounded-lg text-slate-600 text-[12px] hover:bg-white">Cancel</button>
              <button onClick={() => assignee && act(() => mntAPI.assignWO(wo.id, { assigned_to: assignee }), "Assigned")}
                      disabled={!assignee || busy} data-testid="wo-assign-confirm"
                      className="h-8 px-3 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[12px] font-medium disabled:opacity-50">Assign</button>
            </div>
          </div>
        )}

        {/* Complete panel */}
        {showComplete && (
          <div className="rounded-lg bg-emerald-50/60 border border-emerald-100 p-3 space-y-2">
            <div className="text-[12px] font-medium text-hocp-navy">Complete Work Order</div>
            <textarea rows={2} value={resolveNotes} onChange={e => setResolveNotes(e.target.value)}
                      data-testid="wo-complete-notes"
                      placeholder="Resolution notes (required)"
                      className={cls + " py-2 h-auto"}/>
            <div className="flex gap-2 items-center">
              <span className="text-[11.5px] text-slate-500">Labour minutes</span>
              <NumberInput value={labourMin} onChange={e => setLabourMin(e.target.value)}
                     data-testid="wo-complete-labour"
                     className="w-24 h-8 px-2 rounded-md border border-slate-200 text-[13px] tabular-nums"/>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowComplete(false)} className="h-8 px-3 rounded-lg text-slate-600 text-[12px] hover:bg-white">Cancel</button>
              <button onClick={() => resolveNotes && act(() => mntAPI.completeWO(wo.id, { resolution_notes: resolveNotes, labour_minutes: Number(labourMin) }), "Completed")}
                      disabled={!resolveNotes || busy} data-testid="wo-complete-confirm"
                      className="h-8 px-3 rounded-lg bg-emerald-600 text-white text-[12px] font-medium disabled:opacity-50">Complete</button>
            </div>
          </div>
        )}
      </div>

      {/* Actions footer */}
      <div className="pt-4 mt-4 border-t border-slate-100 flex flex-wrap justify-end gap-2 -mx-5 -mb-5 px-5 py-3 bg-slate-50">
        {wo.status === "submitted" && (
          <button onClick={() => setShowAssign(true)} data-testid="wo-action-assign"
                  className="h-9 px-3.5 rounded-lg bg-sky-600 text-white text-[13px] font-medium inline-flex items-center gap-1.5">
            <Play className="w-4 h-4"/> Assign
          </button>
        )}
        {wo.status === "assigned" && (
          <button onClick={() => act(() => mntAPI.startWO(wo.id), "Started")}
                  disabled={busy} data-testid="wo-action-start"
                  className="h-9 px-3.5 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-1.5">
            <Play className="w-4 h-4"/> Start
          </button>
        )}
        {wo.status === "in_progress" && (
          <>
            <button onClick={() => act(() => mntAPI.pauseWO(wo.id, {}), "Paused")}
                    disabled={busy} data-testid="wo-action-pause"
                    className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px] font-medium hover:bg-slate-100 inline-flex items-center gap-1.5">
              <Pause className="w-4 h-4"/> Pause
            </button>
            <button onClick={() => setShowComplete(true)} data-testid="wo-action-complete"
                    className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-[13px] font-medium inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4"/> Complete
            </button>
          </>
        )}
        {wo.status === "paused" && (
          <button onClick={() => act(() => mntAPI.startWO(wo.id), "Resumed")}
                  disabled={busy} data-testid="wo-action-resume"
                  className="h-9 px-3.5 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-1.5">
            <Play className="w-4 h-4"/> Resume
          </button>
        )}
        {wo.status === "completed" && (
          <button onClick={() => act(() => mntAPI.closeWO(wo.id), "Closed")}
                  disabled={busy} data-testid="wo-action-close"
                  className="h-9 px-3.5 rounded-lg bg-slate-700 text-white text-[13px] font-medium inline-flex items-center gap-1.5">
            <Lock className="w-4 h-4"/> Close
          </button>
        )}
        {["submitted","assigned","in_progress","paused"].includes(wo.status) && (
          <button onClick={() => {
                    const reason = prompt("Cancel reason?");
                    if (reason) act(() => mntAPI.cancelWO(wo.id, { reason }), "Cancelled");
                  }} disabled={busy} data-testid="wo-action-cancel"
                  className="h-9 px-3.5 rounded-lg text-rose-700 text-[13px] font-medium hover:bg-rose-50 inline-flex items-center gap-1.5">
            <XCircle className="w-4 h-4"/> Cancel
          </button>
        )}
      </div>
    </Modal>
  );
}

// ── Local UI primitives ────────────────────────────────────────────────
export const cls = "w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px] outline-none focus:border-hocp-navy transition";
export function F({ label, children, full }) {
  return <label className={`block ${full ? "col-span-2" : ""}`}><div className="text-[11.5px] text-slate-500 mb-1 font-medium">{label}</div>{children}</label>;
}
export function Info({ label, children }) {
  return <div><div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</div><div className="text-[13px] text-hocp-navy font-medium">{children}</div></div>;
}
export function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl w-full ${wide ? "max-w-3xl" : "max-w-2xl"} overflow-hidden max-h-[92vh] flex flex-col`}
           onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">{title}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4 text-slate-500"/></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
}
export function Footer({ onClose, onSubmit, saving, testid }) {
  return (
    <div className="pt-4 mt-4 border-t border-slate-100 flex justify-end gap-2 -mx-5 -mb-5 px-5 py-3 bg-slate-50">
      <button onClick={onClose} className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px] hover:bg-slate-100">Cancel</button>
      <button onClick={onSubmit} disabled={saving} data-testid={testid}
              className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium disabled:opacity-60 inline-flex items-center gap-2">
        <Save className="w-4 h-4"/> {saving ? "Saving…" : "Submit"}
      </button>
    </div>
  );
}
